import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiPaginatedResponse } from "@/types";
import type {
  ICompanyPoliciesStats,
  ISingleCompanyPolicies,
} from "@/types/(my-space)/company-policies.interface";

import { createApi } from "@reduxjs/toolkit/query/react";

export const MSCompanyPoliciesApi = createApi({
  reducerPath: "MSCompanyPoliciesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "GetAllCompanyPolicies",
    "GetCompanyPoliciesStats",
    "GetCompanyPolicyById",
  ],

  endpoints: (builder) => ({
    // ✅ Get All Policies
    getAllCompanyPolicies: builder.query<
      IApiPaginatedResponse<ISingleCompanyPolicies[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.CompanyPolicies.GetAllCompanyPolicies,
        method: "GET",
        params,
      }),
      providesTags: ["GetAllCompanyPolicies"],
    }),

    // ✅ Stats
    getCompanyPoliciesStats: builder.query<
      IApiPaginatedResponse<ICompanyPoliciesStats>,
      void
    >({
      query: () => ({
        url: APIEndpoints.MySpace.CompanyPolicies.GetCompanyPoliciesStats,
        method: "GET",
      }),
      providesTags: ["GetCompanyPoliciesStats"],
    }),

    // ✅ Get By ID
    getCompanyPolicyById: builder.query<
      IApiPaginatedResponse<ISingleCompanyPolicies>,
      { companyPoliciesId: string }
    >({
      query: ({ companyPoliciesId }) => ({
        url: APIEndpoints.MySpace.CompanyPolicies.GetCompanyPoliciesById(
          companyPoliciesId,
        ),
        method: "GET",
      }),
      providesTags: ["GetCompanyPolicyById"],
    }),

    // ✅ Acknowledge Policy
    acknowledgeCompanyPolicy: builder.mutation<
      IApiPaginatedResponse<void>,
      { companyPoliciesId: string }
    >({
      query: ({ companyPoliciesId }) => ({
        url: APIEndpoints.MySpace.CompanyPolicies.AcknowledgeCompanyPolicies(
          companyPoliciesId,
        ),
        method: "POST",
      }),

      invalidatesTags: ["GetAllCompanyPolicies", "GetCompanyPoliciesStats"],
    }),
  }),
});

export const {
  useGetAllCompanyPoliciesQuery,
  useGetCompanyPoliciesStatsQuery,
  useGetCompanyPolicyByIdQuery,
  useAcknowledgeCompanyPolicyMutation,
} = MSCompanyPoliciesApi;
